export const OVERLAY_THEME2_STYLES_URL =
  "https://superplayerauction.com/manage/ovcss/style2.css";

let overlayTheme2CssTextCache = null;
let overlayTheme2CssTextPromise = null;

/** Fetch style9.css once; shared by preview inject + picker defaults */
export function fetchOverlayTheme2CssText() {
  if (overlayTheme2CssTextCache) {
    return Promise.resolve(overlayTheme2CssTextCache);
  }

  if (!overlayTheme2CssTextPromise) {
    overlayTheme2CssTextPromise = fetch(OVERLAY_THEME2_STYLES_URL)
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Failed to fetch overlay theme 2 stylesheet (${response.status})`,
          );
        }
        return response.text();
      })
      .then((text) => {
        overlayTheme2CssTextCache = text;
        return text;
      })
      .catch((error) => {
        overlayTheme2CssTextPromise = null;
        throw error;
      });
  }

  return overlayTheme2CssTextPromise;
}

/** React state → CSS variable on .overlay-1 */
export const OV2_STATE_TO_CSS_VAR = {
  primarycolor_gc1: "--primarycolor-gc1",
  primarycolor_gc2: "--primarycolor-gc2",
  secondarycolor: "--secondarycolor",
  primaryfontcolor: "--primaryfont",
  sold_bg: "--sold-bg",
  sold_gc1: "--sold-gc1",
  sold_gc2: "--sold-gc2",
  sold_team_name: "--sold-team-name",
  unsold_bg: "--unsold-bg",
  unsold_gc1: "--unsold-gc1",
  unsold_gc2: "--unsold-gc2",
};

/** Derived vars set alongside wired colors */
export const OV2_DERIVED_CSS_VARS = {
  primarycolor_gc1: ["--primarycolor-solid"],
  secondarycolor: ["--t-point-bg"],
};

/** CSS variable → React state */
export const OV2_CSS_VAR_TO_STATE = {
  "--primarycolor-gc1": "primarycolor_gc1",
  "--primarycolor-gc2": "primarycolor_gc2",
  "--secondarycolor": "secondarycolor",
  "--primaryfont": "primaryfontcolor",
  "--sold-bg": "sold_bg",
  "--sold-gc1": "sold_gc1",
  "--sold-gc2": "sold_gc2",
  "--sold-team-name": "sold_team_name",
  "--unsold-bg": "unsold_bg",
  "--unsold-gc1": "unsold_gc1",
  "--unsold-gc2": "unsold_gc2",
};

/** Reset UI key → CSS vars / state */
export const OV2_RESET_KEY_TO_CSS_VARS = {
  th2_primecolor: [
    { cssVar: "--primarycolor-gc1", stateKey: "primarycolor_gc1" },
    { cssVar: "--primarycolor-gc2", stateKey: "primarycolor_gc2" },
  ],
  sec_color: [{ cssVar: "--secondarycolor", stateKey: "secondarycolor" }],
  primefont_color: [
    { cssVar: "--primaryfont", stateKey: "primaryfontcolor" },
  ],
  th2_sold_mix: [
    { cssVar: "--sold-gc1", stateKey: "sold_gc1" },
    { cssVar: "--sold-gc2", stateKey: "sold_gc2" },
  ],
  th2_sold_bg: [{ cssVar: "--sold-bg", stateKey: "sold_bg" }],
  th2_sold_team: [
    { cssVar: "--sold-team-name", stateKey: "sold_team_name" },
  ],
  th2_unsold_mix: [
    { cssVar: "--unsold-gc1", stateKey: "unsold_gc1" },
    { cssVar: "--unsold-gc2", stateKey: "unsold_gc2" },
  ],
  th2_unsold_bg: [{ cssVar: "--unsold-bg", stateKey: "unsold_bg" }],
};

/**
 * API ov_custom field → React state.
 * Note: submit saves font as `primaryfont`, older data may use `primaryfontcolor`.
 */
export const OV2_API_TO_STATE = {
  primarycolor_gc1: "primarycolor_gc1",
  primarycolor_gc2: "primarycolor_gc2",
  secondarycolor: "secondarycolor",
  primaryfont: "primaryfontcolor",
  primaryfontcolor: "primaryfontcolor",
  sold_bg: "sold_bg",
  sold_gc1: "sold_gc1",
  sold_gc2: "sold_gc2",
  sold_team_name: "sold_team_name",
  unsold_bg: "unsold_bg",
  unsold_gc1: "unsold_gc1",
  unsold_gc2: "unsold_gc2",
};

export const OV2_WIRED_STATE_KEYS = Object.keys(OV2_STATE_TO_CSS_VAR);

export function getAllOverlayTheme2CssVars() {
  const vars = [...Object.values(OV2_STATE_TO_CSS_VAR)];
  Object.values(OV2_DERIVED_CSS_VARS).forEach((list) => {
    list.forEach((v) => {
      if (!vars.includes(v)) vars.push(v);
    });
  });
  return vars;
}

const NAMED_CSS_COLORS = {
  darkblue: "#00008b",
  black: "#000000",
  white: "#ffffff",
  red: "#ff0000",
  green: "#008000",
  blue: "#0000ff",
};

export function cssVarValueToHex(value) {
  if (!value) return "";
  const v = value.trim();
  if (v.startsWith("#")) {
    return v.length === 4
      ? `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`
      : v.slice(0, 7);
  }

  const rgb = v.match(
    /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*[\d.]+)?\s*\)/,
  );
  if (rgb) {
    const hex = (n) => Number(n).toString(16).padStart(2, "0");
    return `#${hex(rgb[1])}${hex(rgb[2])}${hex(rgb[3])}`;
  }

  const named = NAMED_CSS_COLORS[v.toLowerCase()];
  if (named) return named;

  return v;
}

/** Parse color CSS variables from style9.css :root, .overlay-1 block */
export function parseOverlayTheme2CssVars(cssText) {
  const block = cssText.match(/:root\s*,\s*\.overlay-1\s*\{([^}]+)\}/);
  if (!block) return {};

  const vars = {};
  for (const [, name, value] of block[1].matchAll(
    /--([\w-]+)\s*:\s*([^;]+)/g,
  )) {
    const v = value.trim();
    if (!v.startsWith("url(") && !v.startsWith("var(") && !v.startsWith("linear-gradient")) {
      vars[`--${name}`] = v;
    }
  }
  return vars;
}

export function buildOverlayTheme2VarsFromState(
  state,
  clearedCssVars = new Set(),
) {
  const themeVars = {};

  Object.entries(OV2_STATE_TO_CSS_VAR).forEach(([stateKey, cssVar]) => {
    if (clearedCssVars.has(cssVar)) return;
    const val = state[stateKey];
    if (val) {
      themeVars[cssVar] = val;
      (OV2_DERIVED_CSS_VARS[stateKey] || []).forEach((derived) => {
        if (!clearedCssVars.has(derived)) themeVars[derived] = val;
      });
    }
  });

  return themeVars;
}

export function readOverlayTheme2CssDefault(cssVar, cssFileDefaults, host) {
  const fromFile = cssVarValueToHex(cssFileDefaults?.[cssVar]);
  if (fromFile) return fromFile;

  if (host && typeof getComputedStyle === "function") {
    const root =
      host.shadowRoot?.querySelector("[data-ov-theme2-root]") || host;
    const raw = getComputedStyle(root).getPropertyValue(cssVar).trim();
    const fromHost = cssVarValueToHex(raw);
    if (fromHost) return fromHost;
  }

  return "";
}

export function getOverlayTheme2PickerColor(
  stateKey,
  stateValue,
  cssFileDefaults,
  host = null,
) {
  if (stateValue) return stateValue;

  const cssVar = OV2_STATE_TO_CSS_VAR[stateKey];
  if (!cssVar) return "#ffffff";

  return (
    readOverlayTheme2CssDefault(cssVar, cssFileDefaults, host) || "#ffffff"
  );
}

export function toHexColor(value, fallback = "#ffffff") {
  if (!value) return fallback;
  if (value.startsWith("#")) return value;
  return cssVarValueToHex(value) || fallback;
}

/**
 * Build state + cleared set from API ov_custom.
 * Empty/missing → cleared (style9.css default in preview).
 */
export function buildOverlayTheme2ColorState(apiCustom) {
  const cleared = new Set();
  const stateUpdates = {};

  Object.entries(OV2_API_TO_STATE).forEach(([apiKey, stateKey]) => {
    const apiValue = apiCustom?.[apiKey];
    const cssVar = OV2_STATE_TO_CSS_VAR[stateKey];
    const hasCustom = apiValue != null && String(apiValue).trim() !== "";

    // primaryfontcolor may appear twice (primaryfont + primaryfontcolor)
    if (stateUpdates[stateKey] && !hasCustom) return;

    stateUpdates[stateKey] = hasCustom ? String(apiValue).trim() : "";

    if (cssVar) {
      if (hasCustom) cleared.delete(cssVar);
      else cleared.add(cssVar);
    }
  });

  // Ensure every wired key is represented
  OV2_WIRED_STATE_KEYS.forEach((stateKey) => {
    if (stateUpdates[stateKey] === undefined) {
      stateUpdates[stateKey] = "";
      const cssVar = OV2_STATE_TO_CSS_VAR[stateKey];
      if (cssVar) cleared.add(cssVar);
    }
  });

  return { stateUpdates, cleared };
}

/** All wired theme-2 colors cleared → style9.css defaults only */
export function buildClearedOverlayTheme2ColorState() {
  return buildOverlayTheme2ColorState(null);
}

export function applyOverlayTheme2VarsToRoot(root, themeVars, clearedCssVars) {
  if (!root) return;

  getAllOverlayTheme2CssVars().forEach((cssVar) => {
    if (clearedCssVars?.has(cssVar) || !themeVars?.[cssVar]) {
      root.style.removeProperty(cssVar);
    }
  });

  Object.entries(themeVars || {}).forEach(([cssVar, value]) => {
    if (clearedCssVars?.has(cssVar)) return;
    if (value) root.style.setProperty(cssVar, value);
  });
}
